import React, { useEffect, useState } from 'react';
import {
    aig_audit_capas, aig_audit_create_capa, aig_audit_update_capa,
    aig_audit_delete_capa, aig_audit_capa_history, aig_audit_bulk_update_capas,
    aig_audit_capa_findings, aig_audit_capa_assignable_users, aig_audit_capa_export,
} from '../services';

// Extracted from AuditPortal.js on 2026-05-07. AuditPortal had grown
// past 7100 LOC and the file-size hygiene rule (≤ ~1500 per module)
// asked for a phased split. CAPA Tracker is a self-contained surface
// so it lifts cleanly without touching neighbouring views.
//
// Props:
//   flash(msg)  — toast helper from the parent
//   audits      — array used to populate the Audit dropdown in the
//                 create modal (avoids a duplicate fetch).

const fmtDate = (ts) => {
    if (!ts) return null;
    try { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ts; }
};

const STATUS_OPTIONS = [
    ['open', 'Open'], ['in_progress', 'In Progress'],
    ['implemented', 'Implemented'], ['verified', 'Verified'],
    ['closed', 'Closed'],
];

export default function CAPATracker({ flash, audits = [] }) {
    const [capas, setCapas] = useState([]);
    const [capaSummary, setCapaSummary] = useState({});
    const [capaFindings, setCapaFindings] = useState([]);
    const [capaUsers, setCapaUsers] = useState([]);
    const [selectedCapaIds, setSelectedCapaIds] = useState([]);
    const [bulkCapaStatus, setBulkCapaStatus] = useState('in_progress');
    const [historyCapa, setHistoryCapa] = useState(null);
    const [capaHistory, setCapaHistory] = useState([]);
    const [showCapaModal, setShowCapaModal] = useState(false);
    const [capaForm, setCapaForm] = useState({
        title: '', description: '', capa_type: 'corrective',
        root_cause: '', action_plan: '', expected_outcome: '',
        assigned_to_id: '', due_date: '', finding_id: '', audit_id: '',
    });

    // One helper per endpoint — avoids the inline-await drift that
    // caused the Audit Calendar crash on 2026-05-05.
    const loadCapas = async () => {
        try {
            const r = await aig_audit_capas();
            const d = r?.statusCode?.response_data?.data;
            setCapas(Array.isArray(d?.capas) ? d.capas : []);
            setCapaSummary(d?.summary || {});
        } catch (e) {
            setCapas([]); setCapaSummary({});
        }
    };
    const loadCapaFindings = async (auditId) => {
        try {
            const r = await aig_audit_capa_findings(auditId ? { audit_id: auditId } : null);
            const d = r?.statusCode?.response_data?.data;
            setCapaFindings(Array.isArray(d?.findings) ? d.findings : []);
        } catch (e) { setCapaFindings([]); }
    };
    const loadCapaAssignableUsers = async () => {
        try {
            const r = await aig_audit_capa_assignable_users();
            const d = r?.statusCode?.response_data?.data;
            setCapaUsers(Array.isArray(d?.users) ? d.users : []);
        } catch (e) { setCapaUsers([]); }
    };
    const loadCapaHistory = async (capaId) => {
        try {
            const r = await aig_audit_capa_history(capaId);
            const d = r?.statusCode?.response_data?.data;
            setCapaHistory(Array.isArray(d?.history) ? d.history : []);
        } catch (e) { setCapaHistory([]); }
    };

    // Single mount-time fetch. Parent used to drive this from the
    // sidebar nav button; now the tracker owns its own load.
    useEffect(() => {
        loadCapas();
        loadCapaAssignableUsers();
    }, []);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>CAPA Tracker</h2>
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Corrective &amp; preventive actions across all audits</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={async () => {
                        try {
                            const r = await aig_audit_capa_export();
                            const blob = r?.data instanceof Blob ? r.data : new Blob([r?.data || ''], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = 'capa-register.csv';
                            document.body.appendChild(a); a.click();
                            document.body.removeChild(a); URL.revokeObjectURL(url);
                        } catch (err) { flash('Failed to export CAPA register.'); }
                    }}>Export CSV</button>
                    <button className="ap-btn ap-btn--primary" onClick={() => {
                        setCapaForm({
                            title: '', description: '', capa_type: 'corrective',
                            root_cause: '', action_plan: '', expected_outcome: '',
                            assigned_to_id: '', due_date: '', finding_id: '', audit_id: '',
                        });
                        setCapaFindings([]);
                        setShowCapaModal(true);
                    }}>+ New CAPA</button>
                </div>
            </div>

            {/* KPI/KRI-style summary tiles driven by backend `summary`. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
                {[
                    { label: 'Total',       value: capaSummary.total       ?? capas.length, color: '#1b2a4a', sub: 'All CAPAs' },
                    { label: 'Open',        value: capaSummary.open        ?? 0,            color: '#dc2626', sub: 'Awaiting work' },
                    { label: 'In Progress', value: capaSummary.in_progress ?? 0,            color: '#d97706', sub: 'Being executed' },
                    { label: 'Implemented', value: capaSummary.implemented ?? 0,            color: '#0ea5e9', sub: 'Pending verify' },
                    { label: 'Verified',    value: capaSummary.verified    ?? 0,            color: '#0f7b4f', sub: 'Sign-off complete' },
                    { label: 'Closed',      value: capaSummary.closed      ?? 0,            color: '#0f7b4f', sub: 'Final state' },
                    { label: 'Overdue',     value: capaSummary.overdue     ?? 0,            color: '#dc2626', sub: 'Past due date' },
                ].map((s, i) => (
                    <div key={i}
                        style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px', transition: 'box-shadow 0.15s, border-color 0.15s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(27,42,74,0.08)'; e.currentTarget.style.borderColor = '#1b2a4a'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#d8dce3'; }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', marginTop: 3 }}>{s.label}</div>
                        <div style={{ fontSize: 9, color: '#7a869a' }}>{s.sub}</div>
                    </div>
                ))}
            </div>

            {selectedCapaIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 8, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#1b2a4a' }}>{selectedCapaIds.length} selected</span>
                    <span style={{ fontSize: 11, color: '#7a869a' }}>Set status to:</span>
                    <select className="ap-input" style={{ width: 'auto', fontSize: 11 }} value={bulkCapaStatus} onChange={e => setBulkCapaStatus(e.target.value)}>
                        {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button className="ap-btn ap-btn--primary" style={{ fontSize: 11 }} onClick={async () => {
                        try {
                            const r = await aig_audit_bulk_update_capas({ ids: selectedCapaIds, status: bulkCapaStatus });
                            const d = r?.statusCode?.response_data?.data;
                            flash(d?.message || `${d?.updated ?? 0} CAPAs updated.`);
                            setSelectedCapaIds([]);
                            loadCapas();
                        } catch (err) { flash('Bulk update failed.'); }
                    }}>Apply</button>
                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={() => setSelectedCapaIds([])}>Clear</button>
                </div>
            )}

            <table className="ap-table">
                <thead><tr>
                    <th style={{ width: 28 }}>
                        <input type="checkbox"
                            checked={capas.length > 0 && selectedCapaIds.length === capas.length}
                            onChange={e => setSelectedCapaIds(e.target.checked ? capas.map(c => c.id) : [])} />
                    </th>
                    <th>CAPA #</th><th>Title</th><th>Type</th><th>Finding</th><th>Audit</th><th>Assigned To</th><th>Due</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>
                    {capas.map(c => (
                        <tr key={c.id} style={c.is_overdue ? { background: 'rgba(220,38,38,0.04)' } : undefined}>
                            <td>
                                <input type="checkbox" checked={selectedCapaIds.includes(c.id)} onChange={e => {
                                    setSelectedCapaIds(e.target.checked
                                        ? [...selectedCapaIds, c.id]
                                        : selectedCapaIds.filter(id => id !== c.id));
                                }} />
                            </td>
                            <td style={{ fontWeight: 600 }}>{c.capa_number}</td>
                            <td>{c.title}</td>
                            <td style={{ textTransform: 'capitalize' }}>{c.capa_type}</td>
                            <td>{c.finding_number}</td>
                            <td>{c.audit_number}</td>
                            <td>{c.assigned_to_name || c.assigned_to_email || '-'}</td>
                            <td>
                                {fmtDate(c.due_date)}
                                {c.is_overdue && <span style={{ marginLeft: 6, fontSize: 9, color: '#dc2626', fontWeight: 700 }}>OVERDUE</span>}
                            </td>
                            <td><span className={`ap-badge ${c.status === 'closed' || c.status === 'verified' ? 'ap-badge--success' : c.status === 'open' ? 'ap-badge--danger' : 'ap-badge--warning'}`}>{c.status}</span></td>
                            <td>
                                {c.status !== 'closed' && c.status !== 'verified' && (
                                    <select style={{ fontSize: 10, padding: '2px 4px', border: '1px solid var(--ap-border)', borderRadius: 3, marginRight: 4 }} value={c.status} onChange={async (e) => {
                                        try {
                                            await aig_audit_update_capa(c.id, { status: e.target.value });
                                            flash('CAPA status updated.');
                                            loadCapas();
                                        } catch (err) { flash('Failed to update CAPA status.'); }
                                    }}>
                                        {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                )}
                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 6px', marginRight: 2 }} title="View activity log" onClick={() => { setHistoryCapa({ id: c.id, capa_number: c.capa_number }); loadCapaHistory(c.id); }}>History</button>
                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 6px', color: '#dc2626' }} onClick={async () => {
                                    if (!window.confirm(`Delete CAPA ${c.capa_number}? This cannot be undone.`)) return;
                                    try {
                                        await aig_audit_delete_capa(c.id);
                                        flash('CAPA deleted.');
                                        loadCapas();
                                    } catch (err) { flash('Failed to delete CAPA.'); }
                                }}>Delete</button>
                            </td>
                        </tr>
                    ))}
                    {capas.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--ap-text-3)' }}>No CAPAs created</td></tr>}
                </tbody>
            </table>

            {showCapaModal && (
                <div className="ap-modal-overlay" onClick={() => setShowCapaModal(false)}>
                    <div className="ap-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New CAPA</h3>

                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Title *</label>
                            <input className="ap-input" type="text" value={capaForm.title} onChange={e => setCapaForm(p => ({ ...p, title: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Description</label>
                            <textarea className="ap-input" rows={2} value={capaForm.description} onChange={e => setCapaForm(p => ({ ...p, description: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Type</label>
                            <select className="ap-input" value={capaForm.capa_type} onChange={e => setCapaForm(p => ({ ...p, capa_type: e.target.value }))}>
                                <option value="corrective">Corrective Action</option>
                                <option value="preventive">Preventive Action</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Root Cause</label>
                            <textarea className="ap-input" rows={2} value={capaForm.root_cause} onChange={e => setCapaForm(p => ({ ...p, root_cause: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Action Plan</label>
                            <textarea className="ap-input" rows={2} value={capaForm.action_plan} onChange={e => setCapaForm(p => ({ ...p, action_plan: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Audit *</label>
                            <select className="ap-input" value={capaForm.audit_id} onChange={e => {
                                const auditId = e.target.value;
                                setCapaForm(p => ({ ...p, audit_id: auditId, finding_id: '' }));
                                if (auditId) loadCapaFindings(auditId); else setCapaFindings([]);
                            }}>
                                <option value="">Select...</option>
                                {audits.map(a => <option key={a.id} value={a.id}>{a.audit_number} - {a.title}</option>)}
                            </select>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Finding *</label>
                            <select className="ap-input" value={capaForm.finding_id} disabled={!capaForm.audit_id} onChange={e => setCapaForm(p => ({ ...p, finding_id: e.target.value }))}>
                                <option value="">{capaForm.audit_id ? (capaFindings.length ? 'Select...' : 'No findings on this audit') : 'Pick an audit first'}</option>
                                {capaFindings.map(f => <option key={f.id} value={f.id}>{f.finding_number} — {f.title} ({f.severity})</option>)}
                            </select>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Assigned To</label>
                            <select className="ap-input" value={capaForm.assigned_to_id} onChange={e => setCapaForm(p => ({ ...p, assigned_to_id: e.target.value }))}>
                                <option value="">Unassigned</option>
                                {capaUsers.map(u => <option key={u.email} value={u.id || ''}>{u.name}{u.role ? ` · ${u.role}` : ''}</option>)}
                            </select>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Due Date</label>
                            <input className="ap-input" type="date" value={capaForm.due_date} onChange={e => setCapaForm(p => ({ ...p, due_date: e.target.value }))} />
                        </div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <button className="ap-btn ap-btn--ghost" onClick={() => setShowCapaModal(false)}>Cancel</button>
                            <button className="ap-btn ap-btn--primary"
                                disabled={!capaForm.title || !capaForm.audit_id || !capaForm.finding_id}
                                onClick={async () => {
                                    try {
                                        await aig_audit_create_capa(capaForm);
                                        flash('CAPA created.');
                                        setShowCapaModal(false);
                                        loadCapas();
                                    } catch (err) { flash('Failed to create CAPA.'); }
                                }}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {historyCapa && (
                <div className="ap-modal-overlay" onClick={() => { setHistoryCapa(null); setCapaHistory([]); }}>
                    <div className="ap-modal" style={{ maxWidth: 560, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Activity Log — {historyCapa.capa_number}</h3>
                            <button onClick={() => { setHistoryCapa(null); setCapaHistory([]); }} style={{ background: 'none', border: 'none', fontSize: 18, color: '#7a869a', cursor: 'pointer' }}>&times;</button>
                        </div>
                        {capaHistory.length === 0 ? (
                            <div style={{ fontSize: 11, color: '#7a869a', padding: 16, textAlign: 'center' }}>No history recorded.</div>
                        ) : (
                            <div>
                                {capaHistory.map(h => (
                                    <div key={h.id} style={{ borderLeft: '2px solid #c7d2fe', paddingLeft: 10, marginBottom: 12 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#1b2a4a' }}>{h.event_label || h.event}</div>
                                        <div style={{ fontSize: 11, color: 'var(--ap-text-2)', marginTop: 2 }}>{h.summary}</div>
                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 3 }}>
                                            {h.actor_name || h.actor_email || 'System'} · {h.created_at?.slice(0, 19).replace('T', ' ')}
                                        </div>
                                        {h.diff && Object.keys(h.diff).length > 0 && (
                                            <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4, padding: 6, background: '#f8fafc', border: '1px solid #e5e9f0', borderRadius: 4 }}>
                                                {Object.entries(h.diff).map(([k, v]) => (
                                                    <div key={k}><strong>{k}:</strong> {v?.old ?? '—'} → {v?.new ?? '—'}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
